import { Component, OnInit, OnDestroy } from "@angular/core";
import {
  FormBuilder,
  Validators,
  FormGroup,
  FormArray,
  AbstractControl,
  ValidatorFn,
} from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DataService } from "src/app/shared/Services/data.service";

@Component({
  selector: 'app-job-order',
  templateUrl: './job-order.component.html',
  styleUrls: ['./job-order.component.scss']
})
export class JobOrderComponent implements OnInit, OnDestroy {
  breadCrumbItems: Array<{}>;
  programData: any = [];
  errmsg: string = "";
  searchTerm: any = "";
  validationform: FormGroup;
  operationData: FormArray;
  editProgId;
  action: any;
  currentPage = 1;
  itemsPerPage = 10;
  pageSize;
  submitted: boolean = false;
  pages: any;
  delprogramId = "";
  user: any;
  custId: any;
  fixedCostData: any = [];
  variableCostData: any = [];
  configCost: any = [];
  operationTempData: any;
  settings = {};
  hoursArray = Array.from({ length: 24 }, (_, i) => i + 1);
  private destroy$ = new Subject<void>();
  processData: any = [];

  constructor(
    private modalService: NgbModal,
    public formBuilder: FormBuilder,
    private dataservice: DataService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.settings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "Unselect All",
      itemsShowLimit: 3,
      allowSearchFilter: true,
    };
    this.user = JSON.parse(sessionStorage.getItem("currentUser"));
    this.custId = this.user["user"]["custId"];
    this.breadCrumbItems = [
      { label: "Configuration" },
      { label: "Job Cards", active: true },
    ];
    this.getProcessData();
    this.getProgramData();
    this.getCost();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getProcessData() {
    this.dataservice
      .get(`api/process?custId=${this.custId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => { 
          this.processData = res || []; 
        },
        (err) => {
          this.toastr.error("Server Unreachable");
          this.errmsg = "Failed to load processes.";
          this.processData = [];
        }
      );
  }

  nonNegativeValidator: ValidatorFn = (control: AbstractControl) => {
    const value = control.value;
    if (value !== null && value !== "" && value !== undefined) {
      const num = Number(value);
      if (isNaN(num)) return { required: true };
      if (num < 0) return { negativeValue: true };
      if (String(value).includes(".")) return { decimalNotAllowed: true };
    }
    return null;
  };

  getCost() {
    this.dataservice.get("api/cost")
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.toastr.clear();
          this.configCost = res || [];
          this.selectType(this.configCost);
        },
        (error) => {
          this.toastr.error("Server Unreachable");
          this.configCost = [];
        }
      );
  }

  selectType(data) {
    this.fixedCostData = [];
    this.variableCostData = [];
    
    data.forEach((elm, index) => {
      if ("fixed" === elm["costType"]) {
        if (elm.costName !== "EnergyCost") {
          this.fixedCostData.push({ item_id: index, item_text: `${elm.costName}` });
        }
      } else if ("variable" === elm["costType"]) {
        this.variableCostData.push({ item_id: index, item_text: `${elm.costName}` });
      }
    });
  }

  getProgramData() {
    this.dataservice.get("jobOrder/masterList/" + this.custId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => { 
          if (res) this.programData = res || []; 
        },
        (error: any) => { 
          this.errmsg = "Server Unreachable";
          this.programData = [];
        }
      );
  }

  onPartNameInput(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    const sanitized = input.replace(/-/g, "");
    this.validationform.get("jobName")?.setValue(sanitized, { emitEvent: false });
  }

  initializeForm() {
    const existingOperationData = this.validationform?.get("operationData") as FormArray;
    if (existingOperationData) {
      while (existingOperationData.length !== 0) {
        existingOperationData.removeAt(0);
      }
    }

    this.validationform = this.formBuilder.group({
      jobDescription: [this.validationform?.value?.jobDescription || ""],
      jobName:        [this.validationform?.value?.jobName || "", [Validators.required]],
      operationData:  this.formBuilder.array([], Validators.required),
    });

    this.addOperation();
  }

  addOperation() {
    const arrayControl = this.validationform.get("operationData") as FormArray;
    const newGroup = this.buildOperationGroup(false, false);
    this.wireOperationGroup(newGroup);
    arrayControl.push(newGroup);
  }

  private costFields(existingData?: any) {
    return {
      operatorCost: [existingData?.operatorCost ?? false],
      fixedCost:    [existingData?.fixedCost || []],
      variableCost: [existingData?.variableCost || []],
    };
  }
  buildOperationGroup(isCycleTime: boolean, isSequential: boolean, existingData?: any): FormGroup {
    const baseId = existingData?.operationId || Math.floor(Math.random() * 900000) + 100000;

    if (isCycleTime && isSequential) {
      return this.formBuilder.group({
        operationId:       [baseId, Validators.required],
        isCycleTime:       [true],
        isSequential:      [true],
        operationName:     [existingData?.operationName || "", [Validators.required]],
        processName:       [existingData?.processName || ""],
        partsPerCycle:     [existingData?.partsPerCycle || "", [Validators.required, this.nonNegativeValidator]],
        countingType:      [existingData?.countingType || "partsPerCycle", Validators.required],
        loadingTime:       [existingData?.loadingTime || "", [Validators.required, this.nonNegativeValidator]],
        unloadingTime:     [existingData?.unloadingTime || "", [Validators.required, this.nonNegativeValidator]],
        standardSetupTime: [existingData?.standardSetupTime || "", [Validators.required, this.nonNegativeValidator]],
        runningTime:       [existingData?.runningTime || 0],
        sequenceData:      this.formBuilder.array([], Validators.required),
        ...this.costFields(existingData),
      });
    } else if (isCycleTime && !isSequential) {
      return this.formBuilder.group({
        operationId:       [baseId, Validators.required],
        isCycleTime:       [true],
        isSequential:      [false],
        operationName:     [existingData?.operationName || "", [Validators.required]],
        processName:       [existingData?.processName || ""],
        partsPerCycle:     [existingData?.partsPerCycle || "", [Validators.required, this.nonNegativeValidator]],
        countingType:      [existingData?.countingType || "partsPerCycle", Validators.required],
        runningTime:       [existingData?.runningTime || "", [Validators.required, this.nonNegativeValidator]],
        loadingTime:       [existingData?.loadingTime || "", [Validators.required, this.nonNegativeValidator]],
        unloadingTime:     [existingData?.unloadingTime || "", [Validators.required, this.nonNegativeValidator]],
        standardSetupTime: [existingData?.standardSetupTime || "", [Validators.required, this.nonNegativeValidator]],
        ...this.costFields(existingData),
      });
    } else {
      return this.formBuilder.group({
        operationId:   [baseId, Validators.required],
        isCycleTime:   [false],
        isSequential:  [false],
        operationName: [existingData?.operationName || "", [Validators.required]],
        processName:   [existingData?.processName || ""],
        partsPerCycle: [existingData?.partsPerCycle || "", [Validators.required, this.nonNegativeValidator]],
        countingType:  [existingData?.countingType || "partsPerCycle", Validators.required],
        target:        [existingData?.target || "", [Validators.required, this.nonNegativeValidator]],
        targetHours:   [existingData?.targetHours || "", Validators.required],
        ...this.costFields(existingData),
      });
    }
  }

  private wireOperationGroup(group: FormGroup) {
    group.get("operationName")?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkDuplicateOperationName();
      });

    group.get("isCycleTime")?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const idx = (this.validationform.get("operationData") as FormArray).controls.indexOf(group);
        this.onOperationCycleTimeChange(idx, val);
      });

    group.get("isSequential")?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const idx = (this.validationform.get("operationData") as FormArray).controls.indexOf(group);
        this.onOperationSequentialChange(idx, val);
      });
  }

  private snapshotCommon(existing: FormGroup) {
    const snapshot: any = {
      operationId:       existing.get("operationId")?.value,
      operationName:     existing.get("operationName")?.value,
      processName:       existing.get("processName")?.value,
      partsPerCycle:     existing.get("partsPerCycle")?.value,
      countingType:      existing.get("countingType")?.value,
      operatorCost:      existing.get("operatorCost")?.value,
      fixedCost:         existing.get("fixedCost")?.value,
      variableCost:      existing.get("variableCost")?.value,
    };

    if (existing.get("loadingTime")) {
      snapshot.loadingTime = existing.get("loadingTime")?.value;
    }
    if (existing.get("unloadingTime")) {
      snapshot.unloadingTime = existing.get("unloadingTime")?.value;
    }
    if (existing.get("standardSetupTime")) {
      snapshot.standardSetupTime = existing.get("standardSetupTime")?.value;
    }
    if (existing.get("runningTime")) {
      snapshot.runningTime = existing.get("runningTime")?.value;
    }

    if (existing.get("target")) {
      snapshot.target = existing.get("target")?.value;
    }
    if (existing.get("targetHours")) {
      snapshot.targetHours = existing.get("targetHours")?.value;
    }

    const sequenceArray = existing.get("sequenceData") as FormArray;
    if (sequenceArray && sequenceArray.length > 0) {
      snapshot.sequenceData = sequenceArray.value;
    }

    return snapshot;
  }

  onOperationCycleTimeChange(index: number, isCycleTime: boolean) {
    const arrayControl = this.validationform.get("operationData") as FormArray;
    const existing = arrayControl.at(index) as FormGroup;
    const isSequential = !!existing.get("isSequential")?.value;

    const snapshot = this.snapshotCommon(existing);
    const newGroup = this.buildOperationGroup(isCycleTime, isCycleTime && isSequential, snapshot);
    this.wireOperationGroup(newGroup);
    arrayControl.setControl(index, newGroup);

    if (isCycleTime && isSequential && snapshot.sequenceData) {
      const sequenceArray = newGroup.get("sequenceData") as FormArray;
      snapshot.sequenceData.forEach((seq: any) => {
        sequenceArray.push(this.createSequenceGroup(index, seq));
      });
      this.calculateTotalRuntime(index);
    }
  }

  onOperationSequentialChange(index: number, isSequential: boolean) {
    const arrayControl = this.validationform.get("operationData") as FormArray;
    const existing = arrayControl.at(index) as FormGroup;
    const isCycleTime = !!existing.get("isCycleTime")?.value;

    const snapshot = this.snapshotCommon(existing);
    const newGroup = this.buildOperationGroup(isCycleTime, isSequential, snapshot);
    this.wireOperationGroup(newGroup);
    arrayControl.setControl(index, newGroup);

    if (isSequential) {
      if (snapshot.sequenceData && snapshot.sequenceData.length > 0) {
        const sequenceArray = newGroup.get("sequenceData") as FormArray;
        snapshot.sequenceData.forEach((seq: any) => {
          sequenceArray.push(this.createSequenceGroup(index, seq));
        });
        this.calculateTotalRuntime(index);
      } else {
        this.addSequenceOperation(index);
      }
    }
  }

  removeOperation(index: number) {
    const arrayControl = this.validationform.get("operationData") as FormArray;
    arrayControl.removeAt(index);
    this.checkDuplicateOperationName();
  }

  addSequenceOperation(operationIndex: number) {
    const operationArray = this.validationform.get("operationData") as FormArray;
    const operationGroup = operationArray.at(operationIndex) as FormGroup;
    const sequenceArray = operationGroup.get("sequenceData") as FormArray;

    const sequenceGroup = this.formBuilder.group({
      seqNumber:   ["", Validators.required],
      runningTime: ["", [Validators.required, this.nonNegativeValidator]],
    });

    sequenceGroup.get("runningTime")?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.calculateTotalRuntime(operationIndex);
      });

    sequenceArray.push(sequenceGroup);
    this.calculateTotalRuntime(operationIndex);
  }

  removeSequence(operationIndex: number, sequenceIndex: number) {
    const operationArray = this.validationform.get("operationData") as FormArray;
    const sequenceArray = (operationArray.at(operationIndex) as FormGroup).get("sequenceData") as FormArray;
    sequenceArray.removeAt(sequenceIndex);
    this.calculateTotalRuntime(operationIndex);
  }

  calculateTotalRuntime(operationIndex: number) {
    const operationArray = this.validationform.get("operationData") as FormArray;
    const operationGroup = operationArray.at(operationIndex) as FormGroup;
    const sequenceArray = operationGroup.get("sequenceData") as FormArray;
    if (!sequenceArray) return;

    const total = sequenceArray.controls.reduce((sum, seq) => {
      return sum + (Number(seq.get("runningTime")?.value) || 0);
    }, 0);

    operationGroup.get("runningTime")?.patchValue(total, { emitEvent: false });
  }

  get form() { 
    return this.validationform.controls; 
  }

  openModal(content: any, data: any, act: any) {
    this.action = act;
    this.submitted = false;

    if (act === "Add") {
      this.initializeForm();
    } else if (act === "Edit") {
      if (data) {
        this.editProgId = data.id;
        this.initializeForm();
        this.pathValue(this.validationform, data);
        ["jobName", "jobDescription"].forEach((key) =>
          this.validationform.get(key)?.disable()
        );
      }
    }

    this.modalService.open(content, { backdrop: "static", centered: true, size: "lg" });
  }

  openModalDelete(deletetemp, data) {
    this.delprogramId = data.id;
    this.modalService.open(deletetemp, { backdrop: "static", centered: true });
  }

  openModalOperation(temp, data) {
    if (data["operationData"] && data["operationData"].length !== 0) {
      this.operationTempData = data["operationData"];
      this.modalService.open(temp, { backdrop: "static", centered: true, size: "lg" });
    } else {
      this.toastr.info("No operations found for this job.");
    }
  }

  createSequenceGroup(operationIndex: number, data?: any): FormGroup {
    const group = this.formBuilder.group({
      seqNumber:   [data ? (data.seqNumber || data.sequenceId) : "", Validators.required],
      runningTime: [data ? data.runningTime : "", [Validators.required, this.nonNegativeValidator]],
    });

    group.get("runningTime")?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.calculateTotalRuntime(operationIndex);
      });

    return group;
  }

  pathValue(formGroup: FormGroup, data: any): void {
    const controls = formGroup.controls;

    for (const key in controls) {
      if (key === "operationData" && Array.isArray(data["operationData"])) {
        const operationArray = controls["operationData"] as FormArray;
        operationArray.clear();

        data["operationData"].forEach((op: any, index: number) => {
          const isCycleTime = !!op.isCycleTime;
          const isSequential = !!(op.sequenceData && op.sequenceData.length > 0);

          const operationGroup = this.buildOperationGroup(isCycleTime, isSequential, op);
          this.wireOperationGroup(operationGroup);

          if (isSequential && op.sequenceData?.length) {
            const sequenceArray = operationGroup.get("sequenceData") as FormArray;
            sequenceArray.clear();
            op.sequenceData.forEach((seq: any) => {
              sequenceArray.push(this.createSequenceGroup(index, seq));
            });
          }

          operationArray.push(operationGroup);
          
          if (isSequential) {
            this.calculateTotalRuntime(index);
          }
        });

        this.checkDuplicateOperationName();

      } else {
        controls[key]?.patchValue(data[key]);
      }
    }
  }

  getPerPartCycleTime(index: number): string {
    const operationArray = this.validationform.get("operationData") as FormArray;
    const group = operationArray.at(index);
    const target = Number(group.get("target")?.value);
    const hours = Number(group.get("targetHours")?.value);

    if (target > 0 && hours > 0) {
      return ((hours * 3600) / target).toFixed(0);
    }
    return "";
  }

  onPageChange(pageNum: number): void {
    this.pageSize = this.itemsPerPage * (pageNum - 1);
  }

  saveData() {
    this.submitted = true;
    if (this.action === "Edit") {
      ["jobName", "jobDescription"].forEach((key) =>
        this.validationform.get(key)?.enable()
      );
    }

    if (this.validationform.valid) {
      this.pages = {
        custId:         this.custId,
        jobName:        this.validationform.value.jobName,
        jobDescription: this.validationform.value.jobDescription,
        operationData:  this.validationform.value.operationData,
      };

      console.log("Saving data:", this.pages);

      if (this.action === "Add") {
        this.dataservice.post("jobOrder/masterList", this.pages).subscribe({
          next: (data) => {
            if (data) {
              this.cancel();
              this.toastr.success("Job Added Successfully");
              this.getProgramData();
            }
          },
          error: (err) => {
            this.toastr.error(err.message || "Failed to add program");
            // Re-disable fields if save failed during edit
            if (this.action === "Edit") {
              ["programId", "jobName", "jobDescription"].forEach((key) =>
                this.validationform.get(key)?.disable()
              );
            }
          },
        });
      } else if (this.action === "Edit") {
        this.dataservice.put("jobOrder/masterList/" + this.editProgId, this.pages).subscribe({
          next: (res) => {
            if (res) {
              this.cancel();
              this.toastr.success("Job Updated Successfully");
              this.getProgramData();
            }
          },
          error: (err) => {
            this.toastr.error(err.message || "Failed to update program");
            ["programId", "jobName", "jobDescription"].forEach((key) =>
              this.validationform.get(key)?.disable()
            );
          },
        });
      }
    } else {
      this.validationform.markAllAsTouched();
      
      const invalidFields: string[] = [];
      const operationArray = this.validationform.get("operationData") as FormArray;
      
      for (const name in this.validationform.controls) {
        if (name !== "operationData" && this.validationform.controls[name].invalid) {
          invalidFields.push(name);
        }
      }

      operationArray.controls.forEach((op, index) => {
        if (op.invalid) {
          invalidFields.push(`Operation ${index + 1}`);
        }
      });

      this.toastr.warning(
        `Please correct validation errors in: ${invalidFields.join(", ")}`,
        "Validation Failed"
      );

      if (this.action === "Edit") {
        ["jobName", "jobDescription"].forEach((key) =>
          this.validationform.get(key)?.disable()
        );
      }
    }
  }

  checkDuplicateOperationName(): void {
    const operationArray = this.validationform.get("operationData") as FormArray;
    const names = operationArray.controls.map(c =>
      String(c.get("operationName")?.value || "").trim().toLowerCase()
    );

    operationArray.controls.forEach((control, index) => {
      const nameControl = control.get("operationName");
      const currentName = String(nameControl?.value || "").trim().toLowerCase();
      const isDuplicate = currentName !== "" &&
                          names.filter(n => n === currentName).length > 1;

      if (isDuplicate) {
        nameControl?.setErrors({ ...nameControl.errors, duplicateName: true });
      } else {
        if (nameControl?.hasError("duplicateName")) {
          const errors = { ...nameControl.errors };
          delete errors["duplicateName"];
          nameControl.setErrors(Object.keys(errors).length ? errors : null);
        }
      }
    });
  }

  cancel() {
    this.submitted = false;
    this.action = "";
    this.editProgId = null;
    
    const operationArray = this.validationform?.get("operationData") as FormArray;
    if (operationArray) {
      operationArray.clear();
    }
    
    this.validationform?.reset();
    this.modalService.dismissAll();
  }

  delete(id) {
    if (!id) {
      this.toastr.error("Invalid program ID");
      return;
    }

    this.dataservice.delete("jobOrder/masterList/" + id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res) {
            this.toastr.success("Job deleted successfully");
            this.modalService.dismissAll();
            this.getProgramData();
          }
        },
        error: (err) => {
          this.toastr.error(err.message || "Failed to delete program");
        }
      });
  }
}